import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { graphql, useStaticQuery } from "gatsby";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import PageTransitionLink from "@components/PageTransitionLink";
import Container from "@components/Container";
import MenuToggle from "@components/MenuToggle";
import HeaderLogo from "@components/HeaderLogo";
import { Caption } from "@components/TextStyles";

import {
  HeaderContainer,
  HeaderMain,
  HeaderMenu,
  HeaderMenuDot,
  HeaderMenuItemAttractionField,
  HeaderMenuItemMain,
  HeaderMenuLink,
  HeaderNav,
  SkipToContent,
  HeaderTop,
  MobileMenu,
  MobileMenuItem,
  MobileNav,
  MobileNavMain,
  HeaderSubMenu,
  HeaderSubMenuMobile,
  SubMenuButton,
  HeaderBack,
  CustomLink,
  ButtonLink,
} from "./index.style";

import { colors } from "@styles/vars/colors.style";

import {
  hideMobileNav,
  setCaseStudyParentSlug,
  setShowHeaderBackground,
  useStore,
} from "@Store/";

import IconArrowDown from "@svgs/IconArrowDown";
import IconBack from "@svgs/IconBack";

gsap.registerPlugin(ScrollTrigger);

const TOOLS_AND_TECHNOLOGY_SUB_MENU = [
  {
    title: <>AudienceGPT</>,
    slug: "about-us/audiencegpt",
  },
  {
    title: <>AdStudio</>,
    slug: "about-us/adstudio",
  },
];

const ABOUT_SUB_MENU = [
  {
    title: "Our Mission",
    slug: "about-us/#our-mission",
    target: "#our-mission",
  },
  {
    title: "Our model",
    slug: "about-us/#our-model",
    target: "#our-model",
  },
  {
    title: <span className="with-caret-icon">OUR TOOLS & TECHNOLOGY <IconArrowDown/></span>,
    slug: "about-us/#our-tools-and-technology",
    target: "#our-tools-and-technology",
    children: TOOLS_AND_TECHNOLOGY_SUB_MENU,
  },
  {
    title: "Our leadership",
    slug: "about-us/#our-leadership",
    target: "#our-leadership",
  },
];

const WHAT_WE_DO_SUB_MENU = [
  {
    title: "Organic Performance",
    slug: "what-we-do/organic-performance",
  },
  {
    title: "Paid Performance",
    slug: "what-we-do/paid-performance",
  },
  {
    title: "Technology & Experience",
    slug: "what-we-do/technology-and-experience",
  },
  {
    title: "Social & Influencer",
    slug: "https://www.socialchain.com/",
    external: true,
  },
  {
    title: "Fan Engagement",
    slug: "https://www.engagedigitalpartners.com/",
    external: true,
  },
  {
    title: "Digital Media Network",
    slug: "what-we-do/digital-media-network",
  },
  {
    title: "Skills & Capabilities",
    slug: "https://minimba.com/",
    external: true,
  },
  {
    title: "Insights & Consultancy",
    slug: "https://wearemtm.com/",
    external: true,
  },
  {
    title: "Growth Consultancy",
    slug: "what-we-do/growth-consultancy",
  },
];

const normalizePathname = (pathname = "") => {
  const pathnameWithoutQuery = pathname.split("?")[0];
  const pathnameWithoutHash = pathnameWithoutQuery.split("#")[0];

  if (
    pathnameWithoutHash.length > 1 &&
    pathnameWithoutHash.endsWith("/")
  ) {
    return pathnameWithoutHash.slice(0, -1);
  }

  return pathnameWithoutHash;
};

const isAboutPath = (pathname) => {
  const normalizedPathname = normalizePathname(pathname);

  return (
    normalizedPathname === "/about" ||
    normalizedPathname === "/about-us"
  );
};

const HeaderSubMenuItem = ({
  item,
  tabIndex,
  pathname,
  onScrollTo,
  onNavigate,
  level,
}) => {
  const hasChildren =
    Array.isArray(item.children) &&
    item.children.length > 0;

  const shouldScroll =
    item.target && isAboutPath(pathname);

  const renderItemLink = () => {
    if (shouldScroll) {
      return (
        <ButtonLink
          type="button"
          data-ref={item.target}
          tabIndex={tabIndex}
          onClick={(event) => {
            onScrollTo(event);

            if (onNavigate) {
              onNavigate();
            }
          }}
        >
          <HeaderMenuItemAttractionField />

          <Caption as="span">
            {item.title}
          </Caption>
        </ButtonLink>
      );
    }

    if (item.external) {
      return (
        <CustomLink
          href={item.slug}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={tabIndex}
          onClick={onNavigate}
        >
          <HeaderMenuItemAttractionField />

          <Caption as="span">
            {item.title}
          </Caption>
        </CustomLink>
      );
    }

    return (
      <PageTransitionLink
        to={item.slug}
        underline={false}
        tabIndex={tabIndex}
        onClick={onNavigate}
      >
        <HeaderMenuItemAttractionField />

        <Caption as="span">
          {item.title}
        </Caption>
      </PageTransitionLink>
    );
  };

  return (
    <div
      data-sub-menu-level={level}
      style={{
        width: "100%",
        paddingRight: level > 0 ? "1.4rem" : 0,
      }}
    >
      {renderItemLink()}

      {hasChildren && (
        <div
          data-nested-sub-menu
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: "0.8rem",
          }}
        >
          {item.children.map((childItem) => (
            <HeaderSubMenuItem
              key={`${item.title}-${childItem.slug}`}
              item={childItem}
              tabIndex={tabIndex}
              pathname={pathname}
              onScrollTo={onScrollTo}
              onNavigate={onNavigate}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

HeaderSubMenuItem.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    target: PropTypes.string,
    external: PropTypes.bool,
    children: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        target: PropTypes.string,
        external: PropTypes.bool,
      }),
    ),
  }).isRequired,
  tabIndex: PropTypes.number,
  pathname: PropTypes.string,
  onScrollTo: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
  level: PropTypes.number,
};

HeaderSubMenuItem.defaultProps = {
  tabIndex: 0,
  pathname: "",
  onNavigate: undefined,
  level: 0,
};

const HeaderMenuItem = ({
  refs,
  index,
  color,
  active,
  faded,
  slug,
  label,
  subMenu,
  tabIndex,
  pathname,
  onScrollTo,
}) => {
  const [store] = useStore();

  const indexRoute = (index - 1) * 2;

  const {
    headerColor,
    headerBackground,
    showHeaderBackground,
    hideHeader,
  } = store;

  const hasSubMenu =
    Array.isArray(subMenu) && subMenu.length > 0;

  return (
    <HeaderMenuItemMain
      color={color}
      hideHeader={hideHeader}
      showHeaderBackground={showHeaderBackground}
      active={active}
      faded={faded}
    >
      {index > 0 && (
        <HeaderMenuDot
          ref={(element) => {
            refs.current[indexRoute + 1] = element;
          }}
        />
      )}

      <HeaderMenuLink
        ref={(element) => {
          refs.current[
            index === 0 ? 0 : indexRoute + 2
          ] = element;
        }}
      >
        <PageTransitionLink
          to={slug}
          underline={false}
          tabIndex={tabIndex}
        >
          <HeaderMenuItemAttractionField />

          <Caption as="span">
            {label}
          </Caption>
        </PageTransitionLink>
      </HeaderMenuLink>

      {hasSubMenu && (
        <HeaderSubMenu
          backgroundColor={headerBackground}
          color={headerColor}
        >
          {subMenu.map((item) => (
            <HeaderSubMenuItem
              key={`${label}-${item.slug}`}
              item={item}
              tabIndex={tabIndex}
              pathname={pathname}
              onScrollTo={onScrollTo}
              level={0}
            />
          ))}
        </HeaderSubMenu>
      )}
    </HeaderMenuItemMain>
  );
};

HeaderMenuItem.propTypes = {
  refs: PropTypes.shape({
    current: PropTypes.array,
  }).isRequired,
  index: PropTypes.number.isRequired,
  color: PropTypes.string,
  active: PropTypes.bool,
  faded: PropTypes.bool,
  slug: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  subMenu: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      target: PropTypes.string,
      external: PropTypes.bool,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string.isRequired,
          slug: PropTypes.string.isRequired,
          target: PropTypes.string,
          external: PropTypes.bool,
        }),
      ),
    }),
  ),
  tabIndex: PropTypes.number,
  pathname: PropTypes.string,
  onScrollTo: PropTypes.func.isRequired,
};

HeaderMenuItem.defaultProps = {
  color: colors.white,
  active: false,
  faded: false,
  subMenu: [],
  tabIndex: 0,
  pathname: "",
};

const Header = forwardRef(({ pathname }, ref) => {
  const [store, dispatch] = useStore();

  const menuItemRefs = useRef([]);
  const backLinkRef = useRef(null);
  const headerItemsLoadAnimation = useRef(null);

  const mobileSubMenuRefs = useRef({});

  const smoothScrollRef = useRef(
    store?.smoothScroll,
  );

  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const [
    activeMobileSubMenu,
    setActiveMobileSubMenu,
  ] = useState(null);

  const {
    mobileNavIsOpen,
    headerColor,
    headerBackground,
    hideHeader,
    showHeaderBackground,
    showPageMask,
    caseStudyParentSlug,
    scrollDirection,
  } = store;

  smoothScrollRef.current = store?.smoothScroll;

  const themeColor = mobileNavIsOpen
    ? colors.white
    : headerColor;

  const {
    contentfulAboutUs,
    contentfulOurWorks,
    contentfulWhatWeDo,
    contentfulCulture,
    contentfulCareers,
    contentfulInsights,
    contentfulContact,
    allCaseStudySlugs,
  } = useStaticQuery(graphql`
    query {
      contentfulAboutUs {
        slug
      }

      contentfulOurWorks {
        slug
      }

      contentfulWhatWeDo {
        slug
      }

      contentfulCulture {
        slug
      }

      contentfulCareers {
        slug
      }

      contentfulInsights {
        slug
      }

      contentfulContact {
        slug
      }

      allCaseStudySlugs: allContentfulCaseStudy {
        nodes {
          slug
        }
      }
    }
  `);

  const menuItems = [
    {
      label: "About Us",
      slug: contentfulAboutUs.slug,
      subMenu: ABOUT_SUB_MENU,
    },
    {
      label: "Our Work",
      slug: contentfulOurWorks.slug,
    },
    {
      label: "What We Do",
      slug: contentfulWhatWeDo.slug,
      subMenu: WHAT_WE_DO_SUB_MENU,
    },
    {
      label: "Culture",
      slug: contentfulCulture.slug,
    },
    {
      label: "Careers",
      slug: contentfulCareers.slug,
    },
    {
      label: "Insights",
      slug: contentfulInsights.slug,
    },
    {
      label: "Contact",
      slug: contentfulContact.slug,
    },
  ];

  const syncScrollTriggers = () => {
    ScrollTrigger.update();

    requestAnimationFrame(() => {
      ScrollTrigger.update();
    });
  };

  const closeMobileNavigation = () => {
    setActiveMobileSubMenu(null);

    if (mobileNavIsOpen) {
      hideMobileNav(dispatch);
    }
  };

  const scrollTos = (event) => {
    event.preventDefault();

    const refId =
      event.currentTarget.getAttribute("data-ref");

    if (
      !refId ||
      isScrollingRef.current ||
      typeof window === "undefined"
    ) {
      return;
    }

    const targetId = refId.replace("#", "");
    const element =
      document.getElementById(targetId);

    if (!element) return;

    const smoothScroll = smoothScrollRef.current;

    isScrollingRef.current = true;

    if (scrollTimeoutRef.current) {
      window.clearTimeout(
        scrollTimeoutRef.current,
      );
    }

    /*
     * Close the mobile navigation before calculating
     * the final target position.
     */
    closeMobileNavigation();

    requestAnimationFrame(() => {
      if (
        smoothScroll &&
        typeof smoothScroll.scrollTo ===
          "function"
      ) {
        try {
          smoothScroll.scrollTo(
            element,
            false,
            "top top",
          );
        } catch (error) {
          const targetTop =
            element.getBoundingClientRect().top +
            window.scrollY -
            60;

          window.scrollTo(0, targetTop);
        }
      } else {
        const targetTop =
          element.getBoundingClientRect().top +
          window.scrollY -
          60;

        window.scrollTo(0, targetTop);
      }

      /*
       * Keep ScrollTrigger progress synchronized without
       * rebuilding pin spacers during the jump.
       */
      syncScrollTriggers();

      scrollTimeoutRef.current =
        window.setTimeout(() => {
          ScrollTrigger.update();
          isScrollingRef.current = false;
        }, 300);
    });
  };

  const toggleMobileSubMenu = (menuKey) => {
    setActiveMobileSubMenu(
      (currentMenuKey) =>
        currentMenuKey === menuKey
          ? null
          : menuKey,
    );
  };

  const isItemActive = (slug) => {
    const normalizedPathname =
      normalizePathname(pathname);

    const normalizedSlug =
      normalizePathname(slug);

    if (normalizedSlug === "/") {
      return normalizedPathname === "/";
    }

    return normalizedPathname.includes(
      normalizedSlug,
    );
  };

  const isItemFaded = (
    slug,
    isMobileItem = false,
  ) => {
    const pageItems = isMobileItem
      ? [...menuItems, { slug: "" }]
      : menuItems;

    const normalizedPathname =
      normalizePathname(pathname);

    const activePageIsInMenu = pageItems.some(
      (item) => {
        const normalizedSlug =
          normalizePathname(item.slug);

        if (normalizedSlug === "/") {
          return normalizedPathname === "/";
        }

        return normalizedPathname.includes(
          normalizedSlug,
        );
      },
    );

    if (!activePageIsInMenu) {
      return false;
    }

    return !isItemActive(slug);
  };

  useEffect(() => {
    Object.entries(
      mobileSubMenuRefs.current,
    ).forEach(([menuKey, element]) => {
      if (!element) return;

      const contentElement =
        element.children[0];

      const isOpen =
        activeMobileSubMenu === menuKey;

      element.style.height =
        isOpen && contentElement
          ? `${contentElement.clientHeight}px`
          : "0px";
    });
  }, [
    activeMobileSubMenu,
    mobileNavIsOpen,
  ]);

  useEffect(() => {
    if (!mobileNavIsOpen) {
      setActiveMobileSubMenu(null);
    }
  }, [mobileNavIsOpen]);

  useLayoutEffect(() => {
    headerItemsLoadAnimation.current =
      gsap.timeline({
        paused: true,
      });

    gsap.set(
      [
        menuItemRefs.current,
        backLinkRef.current,
      ],
      {
        yPercent: -100,
        opacity: 1,
      },
    );

    headerItemsLoadAnimation.current.to(
      menuItemRefs.current,
      {
        yPercent: 0,
        ease: "out",
        duration: 0.15,
        stagger: -0.03,
        delay: 0.15,
      },
    );

    return () => {
      headerItemsLoadAnimation.current?.kill();
    };
  }, []);

  useEffect(() => {
    if (showPageMask) return;

    if (!caseStudyParentSlug) {
      headerItemsLoadAnimation.current?.play();

      gsap.to(backLinkRef.current, {
        yPercent: -100,
        ease: "in",
        duration: 0.15,
      });

      return;
    }

    headerItemsLoadAnimation.current?.reverse();

    gsap.to(backLinkRef.current, {
      yPercent: 0,
      ease: "out",
      delay: 0.7,
      duration: 0.15,
    });
  }, [showPageMask, caseStudyParentSlug]);

  useEffect(() => {
    const caseStudySlugs =
      allCaseStudySlugs.nodes.map(
        (node) => node.slug,
      );

    caseStudySlugs.some((slug) =>
      pathname.includes(`/${slug}`),
    );

    setCaseStudyParentSlug(dispatch, false);
    setShowHeaderBackground(dispatch, false);
  }, [
    dispatch,
    pathname,
    allCaseStudySlugs.nodes,
  ]);

  useEffect(
    () => () => {
      if (scrollTimeoutRef.current) {
        window.clearTimeout(
          scrollTimeoutRef.current,
        );
      }
    },
    [],
  );

  return (
    <HeaderContainer
      ref={ref}
      hideHeader={hideHeader}
      showHeaderBackground={
        showHeaderBackground
      }
      backgroundColor={headerBackground}
      showPageMask={showPageMask}
      scrollDirection={scrollDirection}
    >
      <SkipToContent
        id="skip-to-content"
        href="#smooth-wrapper"
      >
        Skip to content
      </SkipToContent>

      <HeaderTop
        mobileNavIsOpen={mobileNavIsOpen}
      >
        <Container fullWidth>
          <HeaderMain>
            <HeaderLogo
              pathname={pathname}
              hideHeader={hideHeader}
              scrollDirection={scrollDirection}
              title="Bravebison"
              show={showHeaderBackground}
              themeColor={
                showHeaderBackground
                  ? themeColor
                  : hideHeader
                    ? themeColor
                    : colors.white
              }
            />

            {!caseStudyParentSlug && (
              <MenuToggle />
            )}

            <HeaderNav>
              <HeaderMenu>
                {menuItems.map(
                  (item, itemIndex) => (
                    <HeaderMenuItem
                      key={item.label}
                      refs={menuItemRefs}
                      index={itemIndex}
                      color={themeColor}
                      active={isItemActive(
                        item.slug,
                      )}
                      faded={isItemFaded(
                        item.slug,
                      )}
                      slug={item.slug}
                      label={item.label}
                      subMenu={item.subMenu}
                      tabIndex={
                        !caseStudyParentSlug
                          ? 0
                          : -1
                      }
                      pathname={pathname}
                      onScrollTo={scrollTos}
                    />
                  ),
                )}
              </HeaderMenu>
            </HeaderNav>

            {caseStudyParentSlug && (
              <PageTransitionLink
                to="/what-we-do/performance"
                underline={false}
              >
                <HeaderBack color={themeColor}>
                  <span>
                    Back to{" "}
                    <strong>
                      Brave Bison
                    </strong>{" "}
                    Performance
                  </span>

                  <IconBack fill={themeColor} />
                </HeaderBack>
              </PageTransitionLink>
            )}
          </HeaderMain>
        </Container>
      </HeaderTop>

      <MobileNav open={mobileNavIsOpen}>
        <MobileNavMain>
          <MobileMenu>
            <MobileMenuItem
              index={0}
              open={mobileNavIsOpen}
              color={themeColor}
              faded={isItemFaded("/", true)}
            >
              <PageTransitionLink
                to="/"
                onClick={closeMobileNavigation}
              >
                Home
              </PageTransitionLink>
            </MobileMenuItem>

            {menuItems.map((item) => {
              const hasSubMenu =
                Array.isArray(item.subMenu) &&
                item.subMenu.length > 0;

              const menuKey = item.slug;

              const isSubMenuOpen =
                activeMobileSubMenu === menuKey;

              return (
                <MobileMenuItem
                  key={item.label}
                  color={themeColor}
                  faded={isItemFaded(
                    item.slug,
                    true,
                  )}
                >
                  <HeaderMenuDot />

                  <PageTransitionLink
                    to={item.slug}
                    onClick={
                      hasSubMenu
                        ? undefined
                        : closeMobileNavigation
                    }
                  >
                    {item.label}
                  </PageTransitionLink>

                  {hasSubMenu && (
                    <>
                      <SubMenuButton
                        type="button"
                        onClick={() =>
                          toggleMobileSubMenu(
                            menuKey,
                          )
                        }
                        isActive={isSubMenuOpen}
                        aria-expanded={
                          isSubMenuOpen
                        }
                        aria-label={`Toggle ${item.label} submenu`}
                      >
                        <IconArrowDown
                          responsive={false}
                        />
                      </SubMenuButton>

                      <HeaderSubMenuMobile
                        ref={(element) => {
                          mobileSubMenuRefs.current[
                            menuKey
                          ] = element;
                        }}
                        faded={isItemFaded(
                          item.slug,
                          true,
                        )}
                        backgroundColor={
                          headerBackground
                        }
                        color={headerColor}
                      >
                        <div>
                          {item.subMenu.map(
                            (subMenuItem) => (
                              <HeaderSubMenuItem
                                key={`${item.label}-${subMenuItem.slug}`}
                                item={subMenuItem}
                                pathname={pathname}
                                onScrollTo={
                                  scrollTos
                                }
                                onNavigate={
                                  closeMobileNavigation
                                }
                                level={0}
                              />
                            ),
                          )}
                        </div>
                      </HeaderSubMenuMobile>
                    </>
                  )}
                </MobileMenuItem>
              );
            })}
          </MobileMenu>
        </MobileNavMain>
      </MobileNav>
    </HeaderContainer>
  );
});

Header.displayName = "Header";

Header.propTypes = {
  pathname: PropTypes.string,
};

Header.defaultProps = {
  pathname: "",
};

export default Header;